/**
 * SignatureBlock
 *
 * Displays signing/completion information at the bottom of a note as a
 * chronological timeline of signature events.
 *
 * Every entry in signature_metadata is shown in the order it happened —
 * including author re-signs — so the block never hides history (e.g. a
 * re-sign landing after a supervisor co-sign) and the header can never
 * contradict the signatures listed below it.
 *
 * - Uses signature_metadata from the note when available (denormalized credentials)
 * - Falls back to signerName + completionTimestamp when metadata is absent
 * - "Electronically Signed" for signed notes
 * - "Signed — Awaiting Co-signature" when the first co-sign is pending
 * - "Re-signed — Awaiting Co-signature" when the note was re-signed/resubmitted
 *   and is back in the co-sign queue
 * - "Completed" for notes completed without signing
 *
 * Two surfaces render it: the note itself, and the claim workbench's note
 * panel via its `dense` variant (GDN-3343) — so a biller and the note's
 * author read the same signatures, in the same words. Any change to the
 * copy or the event labels here changes both.
 */

import React from 'react';
import { DownloadIcon } from '@jotpsych/ui/icons/DownloadIcon';
import { CheckCircle as CheckCircleIcon, PendingActions as PendingIcon } from '@mui/icons-material';
import { alpha, Box, Divider, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import type { DocumentedByResponse2, SignatureEntryResponse } from '@store/generated/notes';
import { parseUTCDate } from '@utils/dateUtils';
import { formatDate } from '@utils/utils';

import { NoteSectionContainer } from './layouts/NoteSectionContainer';

export interface SignatureBlockProps {
  /** Whether the note was electronically signed (vs just completed) */
  isSigned: boolean;
  /** Fallback display name when signature_metadata is absent */
  signerName: string;
  /** Fallback completion timestamp (Unix seconds) when signature_metadata is absent */
  completionTimestamp?: number;
  /** Denormalized signing entries from the note — used when present */
  signatureMetadata?: SignatureEntryResponse[];
  /** Handler for downloading signed PDF (only shown when provided) */
  onDownloadPdf?: () => void;
  /** Whether the note is awaiting a supervisor co-signature */
  awaitingCosign?: boolean;
  /**
   * GDN-3911: the staff member who drafted this note for the author of record.
   *
   * Rendered above the signature timeline so the block reads "Documented by X
   * · Signed by Y". The distinction is the point: the signer attests to the
   * content, but they did not type it, and a record that hides that cannot be
   * audited later.
   */
  documentedBy?: DocumentedByResponse2 | null;
  /**
   * Compact rendering for a narrow surface (GDN-3343).
   *
   * The claim workbench's note panel shows these same signatures in a
   * resizable column that starts a few hundred pixels wide, and a biller
   * should see what the note's author sees. So the variant lives here rather
   * than in a copy: same events, same labels, same words, less room.
   *
   * What changes is SPACE, never substance. The note page's
   * `NoteSectionContainer` goes (it caps content at ~800px and pads it by
   * `min(80px, 5%)` of the viewport, which inside a 320px column is most of
   * the column), the padding and dividers tighten, and the signer row is
   * allowed to wrap its timestamp instead of squeezing the name. Every
   * credential the full block prints is still printed.
   */
  dense?: boolean;
}

/** A signature entry annotated with its chronological event label. */
interface SignatureEvent {
  entry: SignatureEntryResponse;
  /** "Signed" (first author), "Re-signed" (later author), or "Co-signed" (supervisor) */
  label: string;
}

/**
 * Order every signature entry chronologically and label each one by what
 * happened: the first author entry is the original signature, later author
 * entries are re-signs, and supervisor entries are co-signs.
 */
const buildSignatureEvents = (
  signatureMetadata: SignatureEntryResponse[],
): SignatureEvent[] => {
  const sorted = [...signatureMetadata].sort(
    (a, b) => parseUTCDate(a.signed_at).getTime() - parseUTCDate(b.signed_at).getTime(),
  );

  let authorEntriesSeen = 0;
  return sorted.map(entry => {
    if (entry.role === 'supervisor') {
      return { entry, label: 'Co-signed' };
    }
    authorEntriesSeen += 1;
    return { entry, label: authorEntriesSeen === 1 ? 'Signed' : 'Re-signed' };
  });
};

export const SignatureBlock: React.FC<SignatureBlockProps> = ({
  isSigned,
  signerName,
  completionTimestamp,
  signatureMetadata,
  onDownloadPdf,
  awaitingCosign = false,
  documentedBy = null,
  dense = false,
}) => {
  const theme = useTheme();

  const accentColor = awaitingCosign
    ? theme.palette.warning.main
    : isSigned
      ? theme.palette.success.main
      : theme.palette.grey[500];

  const hasMetadata = signatureMetadata && signatureMetadata.length > 0;
  const events = hasMetadata ? buildSignatureEvents(signatureMetadata) : [];

  // A pending co-sign after any earlier signature activity beyond the original
  // signature (a co-sign already in the history, or an author re-sign) means
  // the note was re-signed/resubmitted back into the co-sign queue — say so.
  const wasResubmitted =
    awaitingCosign && events.some(e => e.label === 'Co-signed' || e.label === 'Re-signed');

  const headerText = awaitingCosign
    ? wasResubmitted
      ? 'Re-signed — Awaiting Co-signature'
      : 'Signed — Awaiting Co-signature'
    : isSigned
      ? 'Electronically Signed'
      : 'Completed';

  /* The dense variant is the same block with less room around it, so the two
     differ only in the numbers below and in whether the note page's layout
     wrapper is there at all. */
  const dividerSx = { my: dense ? 1 : 1.5, borderColor: alpha(accentColor, 0.15) };
  const iconSx = { fontSize: dense ? 15 : 18, color: accentColor };

  const block = (
    <Box
      sx={{
        border: 1,
        borderColor: alpha(accentColor, 0.3),
        borderRadius: dense ? 1.5 : 2,
        bgcolor: alpha(accentColor, 0.04),
        px: dense ? 1.25 : 3,
        py: dense ? 1 : 2.5,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: dense ? 0.75 : 1,
          mb: dense ? 1 : 2,
        }}
      >
        {awaitingCosign ? <PendingIcon sx={iconSx} /> : <CheckCircleIcon sx={iconSx} />}
        <Typography
          variant="overline"
          sx={{
            fontWeight: 600,
            letterSpacing: 1,
            color: accentColor,
            lineHeight: 1,
            flex: 1,
            ...(dense ? { fontSize: '0.625rem' } : {}),
          }}
        >
          {headerText}
        </Typography>
        {onDownloadPdf && (
          <Tooltip title="Download Signed Note" placement="left">
            <IconButton
              onClick={onDownloadPdf}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { color: accentColor },
              }}
              aria-label="Download signed note"
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/*
        GDN-3911: who did the documenting, above who attested to it. Placed
        before the timeline because it happened before every signature in it.
      */}
      {documentedBy?.display_name && (
        <>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: dense ? '0.75rem' : '0.8rem',
            }}
          >
            Documented by{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {documentedBy.display_name}
            </Box>
            {documentedBy.role_label ? ` · ${documentedBy.role_label}` : ''}
          </Typography>
          <Divider sx={dividerSx} />
        </>
      )}

      {/* Chronological signature timeline */}
      {hasMetadata ? (
        events.map((event, index) => (
          <React.Fragment key={index}>
            {index > 0 && <Divider sx={dividerSx} />}
            <SignerRow
              entry={event.entry}
              eventLabel={event.label}
              accentColor={accentColor}
              dense={dense}
            />
          </React.Fragment>
        ))
      ) : (
        <SignerRowFallback name={signerName} timestamp={completionTimestamp} dense={dense} />
      )}

      {/* Pending co-signature row */}
      {awaitingCosign && (
        <>
          <Divider sx={dividerSx} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
              fontStyle: 'italic',
              fontSize: dense ? '0.75rem' : '0.8rem',
            }}
          >
            A supervisor co-signature has been requested and is pending.
          </Typography>
        </>
      )}
    </Box>
  );

  /* The note page's own layout wrapper caps the block at ~800px and pads it
     by a share of the viewport. A panel column supplies its own padding and
     has none to spare, so dense renders the block bare. */
  if (dense) return block;

  return <NoteSectionContainer sx={{ mt: 2, mb: 4 }}>{block}</NoteSectionContainer>;
};

// ============================================================================
// Sub-components
// ============================================================================

interface SignerRowProps {
  entry: SignatureEntryResponse;
  /** Event label for this entry: "Signed", "Re-signed", or "Co-signed" */
  eventLabel: string;
  accentColor: string;
  dense?: boolean;
}

/**
 * Name and timestamp side by side, with the timestamp allowed to wrap under
 * the name in the dense variant: it is `nowrap`, so in a narrow column the
 * only thing that can give is the signer's name, and a truncated clinician
 * name is the one thing this row exists to show.
 */
const signerRowSx = (dense: boolean) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: dense ? 1 : 2,
  ...(dense ? { flexWrap: 'wrap' as const } : {}),
});

const SignerRow: React.FC<SignerRowProps> = ({ entry, eventLabel, dense = false }) => {
  const licenseDisplay =
    entry.license_number && entry.license_state
      ? `${entry.license_number} (${entry.license_state})`
      : entry.license_number || undefined;

  return (
    <Box sx={signerRowSx(dense)}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.65rem',
            fontWeight: 600,
          }}
        >
          {eventLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {entry.name}
        </Typography>
        {entry.npi_number && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            NPI: {entry.npi_number}
          </Typography>
        )}
        {licenseDisplay && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            License: {licenseDisplay}
          </Typography>
        )}
        {entry.license_type && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {entry.license_type}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'right' }}
      >
        {formatDate(parseUTCDate(entry.signed_at).getTime() / 1000, 'both')}
      </Typography>
    </Box>
  );
};

interface SignerRowFallbackProps {
  name: string;
  timestamp?: number;
  dense?: boolean;
}

const SignerRowFallback: React.FC<SignerRowFallbackProps> = ({ name, timestamp, dense = false }) => (
  <Box sx={signerRowSx(dense)}>
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
      {name}
    </Typography>
    {timestamp && (
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'right' }}
      >
        {formatDate(timestamp, 'both')}
      </Typography>
    )}
  </Box>
);
